import { computed, inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs";

import { AuthService } from "../../auth/auth.service";

const LAST_VISITED_ROUTE_KEY = "navigation.lastVisitedRoute";

@Injectable({ providedIn: "root" })
export class NavigationService {
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);

    constructor() {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => {
                this.recordLastVisitedRoute(event.urlAfterRedirects);
            });
    }

    readonly homeRoute = computed<string>(() => {
        if (this.authService.isAdmin() || this.authService.isProducer() || this.authService.isMarketer()) {
            return "/interactives";
        }
        if (this.authService.isHost()) return "/interactives/host";
        return "/gallery";
    });

    postLoginRoute(): string {
        return localStorage.getItem(LAST_VISITED_ROUTE_KEY) ?? this.homeRoute();
    }

    // Also called by the auth guard before redirecting to login, so login returns to a deep link
    // or a home-screen app's page; a home-screen app starts with its own empty storage.
    rememberIntendedRoute(url: string, route: ActivatedRouteSnapshot): void {
        if (deepestChild(route).data["excludeFromLastVisited"]) return;
        localStorage.setItem(LAST_VISITED_ROUTE_KEY, url);
    }

    private recordLastVisitedRoute(url: string): void {
        if (!this.authService.isAuthenticated()) return;
        this.rememberIntendedRoute(url, this.router.routerState.snapshot.root);
    }
}

function deepestChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    return route.firstChild ? deepestChild(route.firstChild) : route;
}
